import type { BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import { planeEval, planeProject } from "geom/plane";
import { planarTopologyPlaneUVAABB } from "./planarTopologyWallCoreResolution";

/**
 * Projects a shareable absolute vertical datum's world Z to topology plane V.
 * Mirrors topologyVToFaceV: pick reference U, project world point at datum Z onto topology plane.
 *
 * Uses V=0 as the reference plane evaluation point. This is correct for current exterior wall
 * topologies where U runs horizontally along the wall and V is vertical; pitched topology planes
 * would need a different projection strategy.
 */
export function worldZToTopologyV(topology: BuildingGraphPlanarTopology, worldZ: number): number {
  const uvAabb = planarTopologyPlaneUVAABB(topology);
  // Pick an arbitrary U on the topology plane. For current exterior wall topologies U is horizontal
  // along the wall run and V is vertical, so this U does not affect the projected topology V.
  const uOnPlane = uvAabb ? (uvAabb.minX + uvAabb.maxX) / 2 : 0;
  const [worldX, worldY] = planeEval(topology.plane, uOnPlane, 0);
  const [, topologyV] = planeProject(topology.plane, worldX, worldY, worldZ);
  return topologyV;
}

/** Inverse of {@link worldZToTopologyV} for vertical wall topologies (U horizontal, V vertical). */
export function topologyVToWorldZ(topology: BuildingGraphPlanarTopology, topologyV: number): number {
  const uvAabb = planarTopologyPlaneUVAABB(topology);
  const uOnPlane = uvAabb ? (uvAabb.minX + uvAabb.maxX) / 2 : 0;
  const [, , worldZ] = planeEval(topology.plane, uOnPlane, topologyV);
  return worldZ;
}
